import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DataObjectIcon from '@mui/icons-material/DataObject'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import HistoryIcon from '@mui/icons-material/History'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import moment from 'moment'
import PropTypes from 'prop-types'
import React from 'react'
import {
    pipelineComm,
    pipelineTemplateId,
    pipelineTemplateName,
} from '../../helpers/pipelines'
import {
    ACCENT,
    FONT,
    LINK,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    tint,
} from '../../theme/tokens'
import RowActions, {
    MAX_VISIBLE_ACTIONS,
} from '../../components/shared/RowActions'
import { channelMeta } from '../PipelineWizard/wizardShared'

// The row actions' column, fixed: at most three 28px icon actions
// (components/shared/RowActions) with 4px gaps. It was `auto`, and each row
// is its own grid, so the header (no actions) and every row sized their
// columns differently and the headings sat over the wrong cells.
const ACTIONS_WIDTH = MAX_VISIBLE_ACTIONS * 28 + (MAX_VISIBLE_ACTIONS - 1) * 4
// Name, Template, Event Type, Channel, Status, Modified By, Created, actions. A pipeline shows no
// category or sub-category: they are its template's, and the Templates page is where they are read.
const GRID_TEMPLATE = `minmax(0, 2fr) minmax(0, 1.6fr) minmax(0, 1.4fr) 0.8fr 0.7fr minmax(0, 1.1fr) 0.9fr ${ACTIONS_WIDTH}px`
// Below this width the columns crush (names cut to one letter); the table
// scrolls instead, with the actions pinned to its right edge. Seven text
// columns and three row actions need about 950px.
export const TABLE_MIN_WIDTH = 950

const ellipsis = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}
const cellText = {
    ...ellipsis,
    typography: 'caption',
    color: TEXT.faint,
    pr: 1,
}

/**
 * The channel as a short coloured tag, from the wizard's channel catalogue
 * ("PUSH", not the raw "PUSH_NOTIFICATION", which overflowed into the Status
 * column; Push and Slack had no colour).
 */
const ChannelBadge = ({ type }) => {
    const meta = channelMeta(type)
    return (
        <Box
            sx={{
                display: 'inline-block',
                px: 0.75,
                py: 0.25,
                borderRadius: `${RADIUS.xs}px`,
                backgroundColor:
                    meta.color === TEXT.muted
                        ? SURFACE.hover
                        : tint(meta.color, 0.12),
                color: meta.color,
                typography: 'overline',
                lineHeight: 1.5,
            }}
        >
            {type ? meta.label.toUpperCase() : '-'}
        </Box>
    )
}

ChannelBadge.propTypes = { type: PropTypes.string }

/**
 * Active or Inactive, from the row's status. There is no pause: "inactive"
 * is a pipeline taken out of service (it was labelled "Paused").
 */
const StatusChip = ({ status }) => {
    const active = status !== 'inactive'
    const color = active ? STATUS.success.fg : TEXT.faint
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
                sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0,
                }}
            />
            <Typography variant="caption" sx={{ color, fontWeight: 500 }}>
                {active ? 'Active' : 'Inactive'}
            </Typography>
        </Box>
    )
}

StatusChip.propTypes = { status: PropTypes.string }

/** The table's column headings. */
export const TableHeaderRow = () => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: GRID_TEMPLATE,
            px: 1.5,
            py: 1,
            borderBottom: `1px solid ${SURFACE.border}`,
            background: SURFACE.subtle,
        }}
    >
        {[
            'Pipeline Name',
            'Template',
            'Event Type',
            'Channel',
            'Status',
            'Modified By',
            'Created',
            '',
        ].map((h, i) => (
            <Typography
                key={i}
                variant="overline"
                sx={{ color: TEXT.faint, lineHeight: 1.6, fontWeight: 700 }}
            >
                {h}
            </Typography>
        ))}
    </Box>
)

/**
 * One pipeline in the list, with its actions.
 *
 * `readOnly`: no Edit, Duplicate or Delete, and the name is not a link.
 * For an inactive pipeline, because the service refuses to update one
 * ("Pipeline is not active"); for a user without the comms manager role,
 * because changing pipelines needs it. Preview, payload and history stay.
 */
export const PipelineRow = ({
    pipeline,
    templates,
    navigate,
    readOnly = false,
    onDelete,
    onDuplicate,
    onPreview,
    onViewPayload,
}) => {
    const channel = pipelineComm(pipeline).comm_type ?? ''
    const templateId = pipelineTemplateId(pipeline)
    const templateName = pipelineTemplateName(pipeline, templates)
    const openEdit = () => navigate(`/pipelines/edit/${pipeline.id}`)
    const modifiedBy = pipeline.modified_by || pipeline.created_by
    // A second line only when someone else created it.
    const createdBy =
        pipeline.modified_by &&
        pipeline.created_by &&
        pipeline.created_by !== pipeline.modified_by
            ? pipeline.created_by
            : ''

    return (
        <Box
            data-testid="pipeline-row"
            sx={{
                display: 'grid',
                gridTemplateColumns: GRID_TEMPLATE,
                px: 1.5,
                py: 1,
                alignItems: 'center',
                borderBottom: `1px solid ${SURFACE.border}`,
                '&:hover': { background: SURFACE.cardHover },
                transition: 'background 0.15s',
            }}
        >
            {readOnly ? (
                <Typography
                    title={pipeline.pipeline_name}
                    sx={{
                        ...cellText,
                        typography: 'body2',
                        color: TEXT.secondary,
                    }}
                >
                    {pipeline.pipeline_name}
                </Typography>
            ) : (
                <Typography
                    role="link"
                    tabIndex={0}
                    title={pipeline.pipeline_name}
                    onClick={openEdit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            openEdit()
                        }
                    }}
                    sx={{
                        ...cellText,
                        typography: 'body2',
                        fontWeight: 500,
                        color: LINK,
                        cursor: 'pointer',
                        outline: 'none',
                        '&:hover': { textDecoration: 'underline' },
                        '&:focus-visible': { textDecoration: 'underline' },
                    }}
                >
                    {pipeline.pipeline_name}
                </Typography>
            )}

            <Typography
                title={templateName || templateId}
                sx={{ ...cellText, color: TEXT.muted }}
            >
                {templateName || (templateId ? 'Unknown template' : '-')}
            </Typography>

            <Typography
                title={pipeline.event_type}
                sx={{ ...cellText, fontFamily: FONT.mono }}
            >
                {pipeline.event_type}
            </Typography>

            <Box>
                <ChannelBadge type={channel} />
            </Box>

            <StatusChip status={pipeline.status} />

            <Box
                sx={{ minWidth: 0, pr: 1 }}
                title={[
                    pipeline.modified_by &&
                        `Modified by ${pipeline.modified_by}`,
                    pipeline.created_by && `Created by ${pipeline.created_by}`,
                ]
                    .filter(Boolean)
                    .join('. ')}
            >
                {/* v2-created rows carry only created_by until edited. */}
                <Typography sx={{ ...cellText, pr: 0 }}>
                    {modifiedBy}
                </Typography>
                {createdBy && (
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{ ...ellipsis, color: TEXT.faint }}
                    >
                        {`Created by ${createdBy}`}
                    </Typography>
                )}
            </Box>

            <Typography variant="caption" sx={{ color: TEXT.faint }}>
                {moment(pipeline.created_on * 1000).format('DD MMM YYYY')}
            </Typography>

            {/* Pinned to the right edge while a narrow window scrolls the
                table sideways, so the actions never sit off screen. */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    position: 'sticky',
                    right: 0,
                    background: SURFACE.card,
                    // A soft edge where rows scroll under the pinned
                    // actions, in either theme.
                    boxShadow: `-12px 0 12px -12px ${SURFACE.backdrop}`,
                }}
            >
                <RowActions
                    name={pipeline.pipeline_name}
                    actions={[
                        !readOnly && {
                            key: 'edit',
                            title: 'Edit',
                            icon: <EditOutlinedIcon />,
                            onClick: openEdit,
                            hoverColor: ACCENT.text,
                        },
                        {
                            key: 'preview',
                            title: 'Preview template',
                            tooltip: templateId
                                ? 'Preview template'
                                : 'No template to preview',
                            note: templateId ? undefined : 'No template',
                            label: `Preview the template of ${pipeline.pipeline_name}`,
                            icon: <VisibilityOutlinedIcon />,
                            disabled: !templateId,
                            onClick: () => onPreview(templateId, channel),
                        },
                        {
                            key: 'payload',
                            title: 'View payload',
                            label: `View the payload of ${pipeline.pipeline_name}`,
                            icon: <DataObjectIcon />,
                            onClick: () => onViewPayload(pipeline.event_type),
                        },
                        !readOnly && {
                            key: 'duplicate',
                            title: 'Duplicate',
                            icon: <ContentCopyIcon />,
                            onClick: () => onDuplicate(pipeline),
                        },
                        // Edits overwrite a pipeline in place; only deletes
                        // leave an inactive version, which is what this
                        // page lists.
                        {
                            key: 'history',
                            title: 'Deleted versions',
                            label: `Deleted versions of ${pipeline.pipeline_name}`,
                            icon: <HistoryIcon />,
                            onClick: () =>
                                navigate(
                                    `/pipeline/history/${encodeURIComponent(
                                        pipeline.pipeline_name
                                    )}`
                                ),
                        },
                        {
                            key: 'insights',
                            title: 'AI Insights',
                            icon: <AutoAwesomeIcon />,
                            onClick: () =>
                                navigate('/ai/performance-insights', {
                                    // Performance Insights reads
                                    // `campaignName`; `campaign_name` left
                                    // the field empty.
                                    state: {
                                        campaignName: pipeline.pipeline_name,
                                        channel,
                                        pipelineID: pipeline.id,
                                    },
                                }),
                        },
                        {
                            key: 'brief',
                            title: 'Campaign Brief',
                            icon: <LightbulbIcon />,
                            onClick: () =>
                                navigate('/ai/campaign-brief', {
                                    state: {
                                        goal: `${pipeline.pipeline_name} pipeline`,
                                        channels: [channel],
                                    },
                                }),
                        },
                        !readOnly && {
                            key: 'delete',
                            title: 'Delete',
                            icon: <DeleteOutlineIcon />,
                            onClick: () => onDelete(pipeline),
                            destructive: true,
                        },
                    ]}
                />
            </Box>
        </Box>
    )
}

PipelineRow.propTypes = {
    pipeline: PropTypes.object.isRequired,
    templates: PropTypes.array,
    navigate: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    onDelete: PropTypes.func.isRequired,
    onDuplicate: PropTypes.func.isRequired,
    onPreview: PropTypes.func.isRequired,
    onViewPayload: PropTypes.func.isRequired,
}
